'use client'
import { ThemeToggle } from '@criteria-labs/react'
import { Disclosure } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { FeedbackButton } from './FeedbackButton'
import { Logo } from './Logo'
import { ModePicker } from './ModePicker'
import { SharePanel } from './SharePanel'
import { ApplicationHeader } from '../components/application'

export function PlaygroundHeader({
  items,
  viewMode,
  setViewMode
}: {
  items: {
    title: string
    href: string
  }[]
  viewMode: 'editor' | 'docs'
  setViewMode: (viewMode: 'editor' | 'docs') => void
}): JSX.Element {
  const activePath = usePathname()
  return (
    <ApplicationHeader>
      <Disclosure as="nav" className="bg-white dark:bg-zinc-950">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-full px-2 sm:px-4">
              <div className="relative flex h-16 justify-between">
                {items.length > 0 && (
                  <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                    {/* Mobile menu button */}
                    <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                      ) : (
                        <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                      )}
                    </Disclosure.Button>
                  </div>
                )}
                <div className="flex flex-1 items-center justify-start sm:items-stretch">
                  <Logo />
                  <SharePanel view={viewMode} className="ml-6" />
                  <div className="hidden sm:ml-12 sm:flex sm:space-x-8">
                    {items.map((item) => {
                      return (
                        <Link
                          key={item.title}
                          href={item.href}
                          className={clsx(
                            activePath && activePath.startsWith(item.href)
                              ? 'border-indigo-500 text-gray-900'
                              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                            'text-md inline-flex items-center border-b-2 px-1 pt-1 font-medium'
                          )}
                        >
                          {item.title}
                        </Link>
                      )
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ModePicker value={viewMode} onChange={setViewMode} />
                  <FeedbackButton />
                  <ThemeToggle data-tooltip-id="playground-header-tooltip" data-tooltip-content="Toggle Theme" />
                </div>
              </div>
            </div>

            <Disclosure.Panel className="sm:hidden">
              <div className="space-y-1 pb-4 pt-2">
                {items.map((item) => {
                  return (
                    <Link key={item.title} href={item.href}>
                      <Disclosure.Button
                        as="a"
                        className={clsx(
                          activePath && activePath.startsWith(item.href)
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700',
                          'block cursor-pointer border-l-4 py-2 pl-3 pr-4 text-base font-medium'
                        )}
                      >
                        {item.title}
                      </Disclosure.Button>
                    </Link>
                  )
                })}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </ApplicationHeader>
  )
}
